import { Platform, StyleSheet } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'

/** Deep base behind the stadium artwork (shows if the SVG layers fail to paint). */
const HERO_BASE = '#0F0A1A'

/** Reused glass tints layered over the floodlit hero (round buttons, pills, flag chip). */
const GLASS_FILL_DARK = 'rgba(0,0,0,0.28)'
const GLASS_BORDER_LIGHT = 'rgba(255,255,255,0.14)'
const GLASS_BORDER_LIGHT_STRONG = 'rgba(255,255,255,0.16)'

/**
 * `windowWidth` from `useWindowDimensions()` drives max width / type scale / spacing.
 * `topInset` lets the floodlit hero bleed under the status bar while content clears it.
 */
export const getLeagueDetailViewStyles = (theme: Theme, windowWidth = 390, topInset = 0) => {
  const comfortable = windowWidth >= 768
  const spacious = windowWidth >= 1024

  const padH = spacious ? 44 : comfortable ? 32 : 16

  const leagueTitleFs = spacious ? 30 : comfortable ? 26 : 23
  const cardRadius = spacious ? 28 : comfortable ? 27 : 26

  return StyleSheet.create({
    scroll: {
      flex: 1,
      backgroundColor: theme.background.primary,
    },
    // Full width on every screen — content spreads across big screens instead of a centred column.
    scrollContent: {
      paddingBottom: spacious ? 48 : comfortable ? 36 : 32,
      paddingHorizontal: padH,
    },

    /** Hero + straddling tab bar — bleeds to the screen edges. The strip beneath the
     *  hero block (behind the tab bar's lower half) uses the body color for the straddle. */
    heroShell: {
      marginHorizontal: -padH,
      paddingHorizontal: padH,
      backgroundColor: theme.background.primary,
    },
    heroBlock: {
      position: 'relative',
      paddingBottom: 30,
      overflow: 'hidden',
      marginHorizontal: -padH,
      paddingHorizontal: padH,
      // Deep base behind the stadium artwork (shows if the SVG layers fail to paint).
      backgroundColor: HERO_BASE,
    },
    heroScrim: {
      ...StyleSheet.absoluteFillObject,
    },
    heroContent: {
      position: 'relative',
      zIndex: 1,
      paddingTop: topInset + (comfortable ? 14 : 10),
    },

    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      marginBottom: spacious ? 16 : 12,
    },
    backButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: GLASS_FILL_DARK,
      borderWidth: 1,
      borderColor: GLASS_BORDER_LIGHT,
    },
    backButtonPressed: {
      opacity: 0.7,
    },

    heroTopRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacious ? 16 : 13,
    },
    leagueHeroFlag: {
      width: 52,
      height: 52,
      borderRadius: 14,
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: GLASS_BORDER_LIGHT_STRONG,
      backgroundColor: 'rgba(0,0,0,0.2)',
    },
    heroTitles: {
      flex: 1,
      minWidth: 0,
    },
    leagueName: {
      fontSize: leagueTitleFs,
      fontWeight: '800',
      color: theme.white,
      letterSpacing: -0.3,
      lineHeight: leagueTitleFs * 1.1,
      textShadowColor: 'rgba(0,0,0,0.4)',
      textShadowOffset: { width: 0, height: 1 },
      textShadowRadius: 8,
    },
    leagueSubtitle: {
      fontSize: comfortable ? 13.5 : 12.5,
      fontWeight: '600',
      color: 'rgba(255,255,255,0.72)',
      marginTop: 3,
    },
    favoriteFab: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: GLASS_FILL_DARK,
      borderWidth: 1,
      borderColor: GLASS_BORDER_LIGHT,
    },
    favoriteFabActive: {
      backgroundColor: 'rgba(252,211,77,0.18)',
      borderColor: 'rgba(252,211,77,0.55)',
    },
    favoriteFabPressed: {
      opacity: 0.7,
    },

    seasonPill: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 6,
      marginTop: spacious ? 18 : 15,
      paddingHorizontal: 13,
      paddingVertical: 8,
      borderRadius: theme.borderRadius.full,
      backgroundColor: 'rgba(0,0,0,0.3)',
      borderWidth: 1,
      borderColor: GLASS_BORDER_LIGHT_STRONG,
    },
    seasonPillText: {
      fontSize: comfortable ? 14 : 13,
      fontWeight: '700',
      color: theme.white,
    },

    /** Glassmorphic tab bar straddles the hero bottom edge. */
    tabStrip: {
      marginTop: -26,
      zIndex: 3,
    },

    /** Body surface holding filters + tab content. */
    mainSurface: {
      backgroundColor: theme.background.primary,
      marginHorizontal: -padH,
      paddingHorizontal: padH,
      paddingTop: spacious ? 22 : 18,
      paddingBottom: spacious ? 28 : 18,
    },
    filterRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacious ? 18 : 14,
      gap: 10,
    },
    standingsGroupBlock: {
      marginBottom: spacious ? 22 : 16,
    },
    standingsGroupHeading: {
      fontSize: spacious ? 16 : 14,
      fontWeight: '800',
      letterSpacing: 0.6,
      color: theme.gray[600],
      marginBottom: spacious ? 10 : 8,
    },
    placeholderCard: {
      backgroundColor: theme.background.card,
      borderRadius: cardRadius,
      borderWidth: 1,
      borderColor: theme.gray[200],
      padding: spacious ? 36 : 28,
      alignItems: 'center',
      ...(Platform.OS === 'web' ? { boxShadow: theme.shadows.card } : {}),
    },
    placeholderTitle: {
      fontSize: spacious ? 19 : 17,
      fontWeight: '700',
      color: theme.black,
      marginBottom: 8,
      textAlign: 'center',
    },
    placeholderBody: {
      fontSize: spacious ? 15 : 14,
      color: theme.gray[600],
      textAlign: 'center',
      lineHeight: spacious ? 22 : 20,
    },
  })
}
